import { useEffect } from 'react'
import { Form, useFormikContext } from 'formik'
import styled from 'styled-components'
import { Checkbox, P, Wrapper, Button } from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'
import FilterPanelWrapper from 'components/list/filter-panel-wrapper'
import { ACTIVE_BRANDS } from 'config/brands'
import { VoucherDiscount } from 'lib/models/voucher'
import { ArrayFilter, FilterValues } from 'hooks/useSearchParamManager'
import { pluralise } from 'utils/helpers'
import { IndexableKey } from 'utils/types/indexable-key'

const FilterHeaders = styled(Wrapper)`
  display: flex;
  align-items: center;
  margin-bottom: ${GTR.S};
  padding-bottom: ${GTR.XS};
  position: relative;
`

const StyledFilter = styled(Wrapper)`
  margin-bottom: ${GTR.L};
`

const StyledCheckbox = styled(Checkbox)`
  margin: ${GTR.S} 0;
`

const StyledWrapper = styled(Wrapper)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${GTR.XS};
  min-height: ${GTR.M};
`

const StyledUnderlineButton = styled(Button.Underline)`
  padding: 0;
  color: ${COLOR.BLACK};
  font-weight: ${FONT.WEIGHT.REGULAR};
  font-size: ${FONT.SIZE.S};
`

const PRODUCT_OPTIONS = [
  { label: 'Funeral', value: 'funeral' },
  { label: 'Funeral plan', value: 'funeral_plan' },
  { label: 'LPA', value: 'lpa' },
  { label: 'Online will', value: 'online_will' },
  { label: 'Probate', value: 'probate' },
  { label: 'Telephone will', value: 'telephone_will' },
]

const VOUCHER_TYPE_OPTIONS = [
  { label: 'Percentage', value: VoucherDiscount.percentage },
  { label: 'Value (pence)', value: VoucherDiscount.valueInPence },
  { label: 'Gift card', value: VoucherDiscount.giftCard },
]

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Expired', value: 'expired' },
]

const FILTER_KEYS = [
  'brandId',
  'productName',
  'discountType',
  'status',
] as const

interface FilterCheckboxProps<T> {
  value: string
  label: string
  filterKey: IndexableKey<T>
}

const FilterCheckbox = <T,>({
  value,
  label,
  filterKey,
}: FilterCheckboxProps<T>) => {
  const { setFieldValue, values } = useFormikContext<FilterValues<T>>()

  const currentValue = values[filterKey] as ArrayFilter

  const handleChange = () => {
    if (currentValue.includes(value)) {
      setFieldValue(
        filterKey,
        currentValue.filter((item) => item !== value)
      )
    } else {
      setFieldValue(filterKey, [...currentValue, value])
    }
  }

  return (
    <StyledCheckbox
      label={label}
      size="S"
      onChange={handleChange}
      checked={currentValue.includes(value)}
    />
  )
}

const FilterPanel = <T,>() => {
  const { handleSubmit, values, setFieldValue } =
    useFormikContext<FilterValues<T>>()

  useEffect(() => {
    handleSubmit()
  }, [handleSubmit, values])

  const resetFilters = () => {
    FILTER_KEYS.forEach((filterKey) => setFieldValue(filterKey, []))
  }

  const appliedFiltersCount = Object.values(values).reduce(
    (count: number, value) =>
      (count += Array.isArray(value) ? value.length : 0),
    0
  )

  return (
    <FilterPanelWrapper>
      <Form>
        <FilterHeaders>
          <P size="M" margin={0} strong>
            Filters
          </P>
        </FilterHeaders>
        <Wrapper margin={[0, 0, GTR.M]}>
          <StyledWrapper>
            {appliedFiltersCount ? (
              <>
                <P size="S" margin={0} color={COLOR.BLACK}>
                  {appliedFiltersCount}{' '}
                  {pluralise(appliedFiltersCount, 'filter')} applied
                </P>

                <StyledUnderlineButton size="S" onClick={resetFilters}>
                  Clear Filters
                </StyledUnderlineButton>
              </>
            ) : null}
          </StyledWrapper>
        </Wrapper>

        <StyledFilter>
          <P strong margin={[0, 0, 'XS']} size="S">
            Brand
          </P>
          {ACTIVE_BRANDS.map((brand) => (
            <FilterCheckbox
              key={`${brand.value}-filter`}
              label={brand.label}
              value={brand.value}
              filterKey="brandId"
            />
          ))}
        </StyledFilter>
        <StyledFilter>
          <P strong margin={[0, 0, 'XS']} size="S">
            Product
          </P>
          {PRODUCT_OPTIONS.map((product) => (
            <FilterCheckbox
              key={`${product.value}-filter`}
              label={product.label}
              value={product.value}
              filterKey="productName"
            />
          ))}
        </StyledFilter>
        <StyledFilter>
          <P strong margin={[0, 0, 'XS']} size="S">
            Voucher type
          </P>
          {VOUCHER_TYPE_OPTIONS.map((voucherType) => (
            <FilterCheckbox
              key={`${voucherType.value}-filter`}
              label={voucherType.label}
              value={voucherType.value}
              filterKey="discountType"
            />
          ))}
        </StyledFilter>
        <StyledFilter>
          <P strong margin={[0, 0, 'XS']} size="S">
            Status
          </P>
          {STATUS_OPTIONS.map((status) => (
            <FilterCheckbox
              key={`${status.value}-filter`}
              label={status.label}
              value={status.value}
              filterKey="status"
            />
          ))}
        </StyledFilter>
      </Form>
    </FilterPanelWrapper>
  )
}

export default FilterPanel
